import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

// Track if we've checked setup status
let setupChecked = false;
let setupRequired = false;

/**
 * Reset the setup cache - call this after setup completes
 * or when you need to force a re-check of setup status
 */
export function resetSetupCache(): void {
  setupChecked = false;
  setupRequired = false;
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/setup',
      name: 'setup',
      component: () => import('@/pages/Setup.vue'),
      meta: { requiresAuth: false, skipSetupCheck: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/Login.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      component: () => import('@/layouts/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/pages/Dashboard.vue'),
        },
        {
          path: 'templates',
          name: 'templates',
          component: () => import('@/pages/Templates.vue'),
        },
        {
          path: 'collections',
          name: 'collections',
          component: () => import('@/pages/Collections.vue'),
        },
        {
          path: 'collections/new',
          name: 'collection-create',
          component: () => import('@/pages/CollectionBuilder.vue'),
        },
        {
          path: 'collections/:collection/edit',
          name: 'collection-edit',
          component: () => import('@/pages/CollectionBuilder.vue'),
        },
        {
          path: 'collections/:collection',
          name: 'items',
          component: () => import('@/pages/Items.vue'),
        },
        {
          path: 'collections/:collection/new',
          name: 'item-create',
          component: () => import('@/pages/ItemEdit.vue'),
        },
        {
          path: 'collections/:collection/:id',
          name: 'item-edit',
          component: () => import('@/pages/ItemEdit.vue'),
        },
        {
          path: 'media',
          name: 'media',
          component: () => import('@/pages/Media.vue'),
        },
        {
          path: 'users',
          name: 'users',
          component: () => import('@/pages/Users.vue'),
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/pages/Settings.vue'),
        },
        {
          path: 'ai-chat',
          name: 'ai-chat',
          component: () => import('@/pages/AIChat.vue'),
        },
        {
          path: 'activity',
          name: 'activity',
          component: () => import('@/pages/ActivityLog.vue'),
        },
        {
          path: 'api-docs',
          name: 'api-docs',
          component: () => import('@/pages/ApiDocs.vue'),
        },
      ],
    },
  ],
});

// Check if setup is required
async function checkSetupStatus(): Promise<boolean> {
  if (setupChecked) return setupRequired;

  try {
    const response = await fetch('/api/setup/status');
    if (response.ok) {
      const data = await response.json() as { setupRequired: boolean };
      setupRequired = data.setupRequired;
    }
  } catch {
    // If setup endpoint fails, assume setup is not required
    setupRequired = false;
  }

  setupChecked = true;
  return setupRequired;
}

// Navigation guard
router.beforeEach(async (to, _from, next) => {
  const auth = useAuthStore();

  // Skip setup check for setup page itself
  if (!to.meta.skipSetupCheck) {
    // Check if setup is required
    const needsSetup = await checkSetupStatus();
    if (needsSetup) {
      next({ name: 'setup' });
      return;
    }
  }

  // Initialize auth state on first navigation
  // This will check the HttpOnly cookie by calling /auth/me
  if (!auth.initialized) {
    await auth.initialize();
  }

  const requiresAuth = to.meta.requiresAuth !== false;

  if (requiresAuth && !auth.user) {
    next({ name: 'login', query: { redirect: to.fullPath } });
  } else if (to.name === 'login' && auth.user) {
    next({ name: 'dashboard' });
  } else {
    next();
  }
});

export default router;
